import axios from 'axios';
require('formdata-polyfill');
import log from '@/configured/logger';
import get from 'lodash.get';
import offline from '@/offline';
import store from '@/store';
import i18n from '@/configured/vue-i18n';
import user from '@/components/user';
import { localeToCraftSiteHandle } from '@/configured/languages/config';

axios.defaults = Object.assign({}, axios.defaults, {
  baseURL: '/',
  withCredentials: true,
});

const globalCancelSource = axios.CancelToken.source();

axios.interceptors.request.use((config) => {
  log.info('Request sending', config.url, config);
  config.cancelToken = globalCancelSource.token;
  return config;
});

// Add a response interceptor
axios.interceptors.response.use(
  function (response) {
    log.trace('Request received', response.request.responseURL, response);
    store.dispatch(`${offline.store.namespace}/setIsOnline`, true);
    return response.data;
  },
  function (error) {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }
    log.error('Request error', error.response, error);
    if (!error.response) {
      store.dispatch(`${offline.store.namespace}/setIsOnline`, false);
    }
    return Promise.reject(error);
  }
);

export default axios;

//////////
export class OfflineError extends Error {
  constructor(innerError) {
    super('Offline');
    this.innerError = innerError;
  }
}

export class SessionExpiredError extends Error {}

export function localizedUrl(endpoint) {
  if (endpoint[0] === '/') {
    endpoint = endpoint.substr(1);
  }
  const locale = store.state[`${i18n.store.namespace}`].language;
  const site = localeToCraftSiteHandle(locale);
  return `/api/${site}/${endpoint}`;
}

export function localizedAndRegionalUrl(endpoint) {
  if (endpoint[0] === '/') {
    endpoint = endpoint.substr(1);
  }
  const locale = store.state[`${i18n.store.namespace}`].language;
  const site = localeToCraftSiteHandle(locale);
  const region = store.getters[`${user.store.namespace}/regionSlug`]();
  return `/api/${site}/${region}/${endpoint}`;
}

export function jsonToMultiPartFormData(json) {
  let formData = new FormData();
  Object.entries(json).forEach((value, index, array) => {
    if (Array.isArray(value[1])) {
      for (let i = 0; i < value[1].length; i++) {
        formData.append(`${value[0]}[]`, value[1][i]);
        log.info('Setting form data', `${value[0]}[]`, value[1][i]);
      }
    } else {
      formData.set(value[0], value[1]);
    }
  });
  return formData;
}

export function verifySession(extend = true) {
  if (store.state['authentication'].working) {
    log.warn('Login working, skipping verifySession');
    return Promise.resolve(1000);
  }
  return getRemainingSessionTime(extend).then(
    (time) => {
      if (time === 0) {
        log.warn('Session expired during verification. Ensuring logout.');
        store.dispatch('authentication/ensureLoggedOut', true);
      } else if (extend) {
        log.info('Extended session', time);
      } else {
        log.info('Verified session', time);
      }
      return time;
    },
    () => {
      if (store.state[offline.store.namespace].isOnline) {
        log.warn('Session request failed while online. Ensuring logout.');
        store.dispatch('authentication/ensureLoggedOut', true);
      } else {
        return 999999;
      }
    }
  );
}

function getRemainingSessionTime(extend) {
  if (store.state[user.store.namespace].sessionTimeout) {
    return Promise.resolve(
      store.state[user.store.namespace].sessionTimeout - Date.now()
    );
  }

  const dne = extend ? '' : '?dontExtendSession=1';
  const cacheBust = new Date().getTime();
  const sessionUrl = '/actions/users/session-info' + dne + (dne ? '&' : '?') + 'cacheBust=' + cacheBust;
  const authUrl = '/api/users/authenticate';

  return axios
    .get(sessionUrl)
    .then((response) => (response && response.timeout ? response.timeout : 0))
    .then(async (time) => {
      if (time === 0 && extend) {
        log.info('Attempting re-authentication via cookie');
        return axios.get(authUrl).then((data) => {
          log.info('Logged in via cookie', { authUrl, data });
          return getRemainingSessionTime(false);
        });
      } else {
        await store.dispatch(`${user.store.namespace}/setSessionTimeout`, time);
      }
      return time;
    });
}

export function csrfRequest() {
  return axios.get('/csrf.json' + '?cacheBust=' + new Date().getTime());
}

export function craftError(response, callback = () => {}) {
  if (response && response.error) {
    callback([{ error: [response.error] }]);
    return true;
  } else if (response && response.errors && response.errors.length > 0) {
    callback(response.errors);
    return true;
  } else if (
    response &&
    response.errors &&
    Object.keys(response.errors).length
  ) {
    let result = [];
    Object.keys(response.errors).forEach((error) => {
      result.push(response.errors[error]);
    });
    callback(result);
    return true;
  }
  return false;
}

// Returns an array with both the current resulting data and
// the next paginate request to call
export function paginate(url) {
  return axios
    .get(url)
    .then((response) => {
      const nextUrl = get(response, 'meta.pagination.links.next', false);
      const urlParts = parsedUrl(nextUrl);
      const newNext = nextUrl
        ? window.location.origin +
          urlParts.pathname +
          urlParts.search +
          urlParts.hash
        : false;
      log.info('Paginate success', nextUrl, newNext);
      return { data: response.data, nextUrl: newNext };
    })
    .catch((error) => {
      if (axios.isCancel(error)) {
        log.info(`Caught cancellation of ${url} request`);
        return { data: null, nextUrl: null };
      }
      throw error;
    });
}

function parsedUrl(url) {
  const link = document.createElement('a');
  link.href = url;
  return link;
}

// Returns a promise that resolves once all paginated urls
// have been completed. Use the collect callback to process
// the response from each paginated call.
export function paginateAll(url, collect) {
  const all = [];

  return paginate(url)
    .then(({ data, nextUrl }) => {
      if (data) {
        all.push(...data);
      }
      collect(data);
      if (nextUrl) {
        return paginateAll(nextUrl, collect);
      } else {
        return all;
      }
    })
    .catch((error) => {
      if (axios.isCancel(error)) {
        info.log(`Caught cancellation of ${url} request`);
        return [];
      }
      throw error;
    });
}

export function cancelAllRequests(message) {
  log.info(`Canceling all requests: ${message}`);
  globalCancelSource.cancel(message);
}

export function toGraphQLQuery(queryString) {
  return queryString.replace(/\n/g, ' ').replace(/\s+/g, ' ');
}

export function getGraphQLHeaders() {
  return {
    // Disable for now
    // Authorization: `Bearer ${process.env.VUE_APP_HOME_API_KEY}`,
    'Content-Type': 'application/json',
  };
}
